import { PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";
import { AppThunk, createAppAsyncThunk } from "appThunk";
import _ from "lodash";
import { RootState } from "store";

import {
    selectDirectCompetitorNames,
    selectPinnedLocation,
    selectPlaceCategories,
    selectSizeBuckets
} from "modules/customer/tools/location/locationSlice";
import { logError } from "modules/helpers/logger/loggerSlice";
import mathUtils from "utils/mathUtils";
import { SortDirection, numberSortExpression } from "utils/sortUtils";

import {
    ClassifiedCompetitor,
    Competitor,
    CompetitorClassification,
    loadCompetitors
} from "./competitor";
import { CompetitorStoreCategory } from "./competitorStoreCategory";
import { selectUserInfo } from "modules/auth/authSlice";

interface CompetitionState {
    isLoading: boolean,
    hasErrors: boolean,
    competitorStoreCategories: CompetitorStoreCategory[],
    competitors: Competitor[],
    showAlternateCategoryCompetitors: boolean
}

interface LoadCompetitionResponse {
    competitorStoreCategories: CompetitorStoreCategory[],
    competitors: Competitor[]
}

const initialState: CompetitionState = {
    isLoading: false,
    hasErrors: false,
    competitorStoreCategories: [],
    competitors: [],
    showAlternateCategoryCompetitors: false
};

const competitionSlice = createSlice({
    name: "customer/tools/location/competitionNew",
    initialState,
    reducers: {
        clearCompetitors: (state) => {
            state.competitorStoreCategories = initialState.competitorStoreCategories;
            state.competitors = initialState.competitors;
        },
        clearShowAlternateCategoryCompetitors: (state) => {
            state.showAlternateCategoryCompetitors = initialState.showAlternateCategoryCompetitors;
        },
        toggleCompetitorStoreCategoryIsSelected: (state, action: PayloadAction<string | number>) => {
            const id = action.payload;
            state.competitorStoreCategories.find(category => category.id === id)?.toggleIsSelected();
        },
        toggleShowAlternateCategoryCompetitors: (state) => {
            state.showAlternateCategoryCompetitors = !state.showAlternateCategoryCompetitors;
        }
    },
    extraReducers: (builder: any) => {
        builder.addCase(loadCompetition.pending, (state: CompetitionState) => {
            state.isLoading = true;
            state.hasErrors = false;
            state.competitorStoreCategories = initialState.competitorStoreCategories;
            state.competitors = initialState.competitors;
        });
        builder.addCase(loadCompetition.rejected, (state: CompetitionState) => {
            state.isLoading = false;
            state.hasErrors = true;
            state.competitorStoreCategories = initialState.competitorStoreCategories;
            state.competitors = initialState.competitors;
        });
        builder.addCase(loadCompetition.fulfilled, (state: CompetitionState, action: PayloadAction<LoadCompetitionResponse>) => {
            state.isLoading = false;
            state.hasErrors = false;
            state.competitorStoreCategories = action.payload.competitorStoreCategories;
            state.competitors = action.payload.competitors;
        });
    }
});

export const {
    clearCompetitors,
    clearShowAlternateCategoryCompetitors,
    toggleCompetitorStoreCategoryIsSelected,
    toggleShowAlternateCategoryCompetitors
} = competitionSlice.actions;

export const loadCompetition = createAppAsyncThunk(
    "customer/tools/location/competitionNew/loadCompetition",
    async (arg, thunkAPI) => {
        try {
            const state = thunkAPI.getState();
            const userInfo = selectUserInfo(state);
            const directCompetitorNames = selectDirectCompetitorNames(state);
            const placeCategories = selectPlaceCategories(state);
            const selectedLocation = selectPinnedLocation(state);

            const selectedLocationPlaceCategoryIds = selectedLocation
                ? [selectedLocation.placeCategoryId, ...selectedLocation.secondaryPlaceCategoryIds]
                : [];
            const primaryAndSecondaryCategories = placeCategories.filter(item => selectedLocationPlaceCategoryIds.includes(item.id));
            const allRelevantPlaceCategoryIds: number[] = primaryAndSecondaryCategories.map(category => category.id);
            allRelevantPlaceCategoryIds.push(...primaryAndSecondaryCategories.flatMap(category => category.indirectlyCompetingCategories));

            const competitorStoreCategories = primaryAndSecondaryCategories.map(category =>
                new CompetitorStoreCategory(category, category.id === selectedLocation?.placeCategoryId, (category.id === selectedLocation?.placeCategoryId ? true : false))
            ).sort((a, b) => numberSortExpression(Number(a.isPrimary), Number(b.isPrimary), SortDirection.DESC));

            const competitorsPromise = thunkAPI.dispatch(loadCompetitors(selectedLocation, directCompetitorNames, allRelevantPlaceCategoryIds, userInfo.accountId));
            const results = await Promise.all([
                competitorsPromise
            ]);

            const loadCompetitionResponse: LoadCompetitionResponse = {
                competitorStoreCategories: competitorStoreCategories,
                competitors: results[0]
            };

            return loadCompetitionResponse;
        } catch (error) {
            thunkAPI.dispatch(logError("Error loading Competition.", error));
            return thunkAPI.rejectWithValue([]);
        }
    }
);

export const clearCompetition = (): AppThunk => (dispatch) => {
    dispatch(competitionSlice.actions.clearCompetitors());
    dispatch(competitionSlice.actions.clearShowAlternateCategoryCompetitors());
};

export const selectIsLoading = (state: RootState) => {
    return state.customer.tools.location.competitionNew.isLoading;
};

export const selectHasErrors = (state: RootState) => {
    return state.customer.tools.location.competitionNew.hasErrors;
};

export const selectCompetitorStoreCategories = (state: RootState) => {
    return state.customer.tools.location.competitionNew.competitorStoreCategories;
};

export const selectCompetitors = (state: RootState) => {
    return state.customer.tools.location.competitionNew.competitors;
};

export const selectShowAlternateCategoryCompetitors = (state: RootState) => {
    return state.customer.tools.location.competitionNew.showAlternateCategoryCompetitors;
};

interface UniqueCompetitor {
    competitor: Competitor,
    id: string,
    storeName: string;
    brandName: string;
    primaryCategoryId: number;
    latitude: number;
    longitude: number;
    sizeBucketId: number;
    alternateCategoryIds: number[];
}

export const selectClassifiedCompetitors = createSelector(
    (state: RootState) => selectPinnedLocation(state),
    (state: RootState) => selectPlaceCategories(state),
    (state: RootState) => selectDirectCompetitorNames(state),
    (state: RootState) => selectSizeBuckets(state),
    selectCompetitorStoreCategories,
    selectShowAlternateCategoryCompetitors,
    selectCompetitors,
    (pinnedLocation, placeCategories, directCompetitorNames, sizeBuckets, competitorStoreCategories, showAlternateCategoryCompetitors, competitors) => {
        if (competitors.length === 0 || competitorStoreCategories.length === 0) {
            return [];
        }
        const selectedPlaceCategories = competitorStoreCategories.filter(category => category.isSelected());
        const selectedPlaceCategoryIds = selectedPlaceCategories.map(category => category.id);
        const selectedIndirectlyCompetingCategoryIds = selectedPlaceCategories.flatMap(category => category.indirectlyCompetingCategories);
        const allRelevantPlaceCategoryIds = [...selectedPlaceCategoryIds, ...selectedIndirectlyCompetingCategoryIds];
        const directCompetitorNamesLowerCase = directCompetitorNames.map(name => name.toLowerCase());

        const uniqueCompetitors: UniqueCompetitor[] = _(competitors)
            .groupBy(competitor => competitor.id)
            .map(group => {
                const alternateCategoryIds = Array.from(new Set(group.flatMap(item => item.alternateCategoryIds)));
                return {
                    competitor: group.find(item => !item.isAlternateCategory) ?? group[0],
                    id: group[0].id,
                    storeName: group[0].storeName,
                    brandName: group[0].brandName,
                    primaryCategoryId: group[0].primaryCategoryId,
                    latitude: group[0].latitude,
                    longitude: group[0].longitude,
                    sizeBucketId: group[0].sizeBucketId,
                    alternateCategoryIds
                };
            })
            .value();

        const newCompetitors = uniqueCompetitors
            .filter(competitor =>
                directCompetitorNamesLowerCase.includes(competitor.storeName.toLowerCase())
                || directCompetitorNamesLowerCase.includes(competitor.brandName.toLowerCase())
                || allRelevantPlaceCategoryIds.includes(competitor.primaryCategoryId)
                || (showAlternateCategoryCompetitors && selectedPlaceCategoryIds.some(item => competitor.alternateCategoryIds.includes(item))))
            .map(competitor => {
                const classification = (directCompetitorNamesLowerCase.includes(competitor.storeName.toLowerCase())
                    || directCompetitorNamesLowerCase.includes(competitor.brandName.toLowerCase()))
                    ? CompetitorClassification.Direct
                    : selectedPlaceCategoryIds.includes(competitor.primaryCategoryId)
                        ? CompetitorClassification.Standard
                        : (showAlternateCategoryCompetitors && selectedPlaceCategoryIds.some(item => competitor.alternateCategoryIds.includes(item)))
                            ? CompetitorClassification.Alternate
                            : CompetitorClassification.Indirect;
                const distanceToSelectedLocation = mathUtils.haversineDistance(
                    pinnedLocation?.latitude ?? 0,
                    pinnedLocation?.longitude ?? 0,
                    competitor?.latitude ?? 0,
                    competitor?.longitude ?? 0
                );
                const placeCategory = placeCategories.find(category => category.id === competitor.primaryCategoryId);
                const displayCategoryName = placeCategory?.name ?? "Unknown";
                const alternateCategoryNames = placeCategories
                    .filter(category => competitor.alternateCategoryIds.includes(category.id))
                    .map(category => category.name)
                    .join(', ');
                const sizeBucket = sizeBuckets.find(item => item.id === competitor.sizeBucketId);

                return new ClassifiedCompetitor(
                    competitor.competitor,
                    classification,
                    displayCategoryName,
                    alternateCategoryNames,
                    distanceToSelectedLocation,
                    sizeBucket
                );
            })
            .sort((a, b) => numberSortExpression(a.distanceToSelectedLocation, b.distanceToSelectedLocation, SortDirection.ASC));
        return newCompetitors;
    }
);

interface CompetitorCounts {
    competitors: number,
    directCompetitors: number,
    indirectCompetitors: number
}

export const selectClassifiedCompetitorCounts = createSelector(
    selectClassifiedCompetitors,
    selectShowAlternateCategoryCompetitors,
    (selectedStoreCompetitors, showAlternateCategoryCompetitors) => {
        const selectedStoreCount: CompetitorCounts = {
            competitors: selectedStoreCompetitors.filter(
                store => (showAlternateCategoryCompetitors
                    ? store.classification === CompetitorClassification.Standard || store.classification === CompetitorClassification.Alternate
                    : store.classification === CompetitorClassification.Standard)
            ).length,
            directCompetitors: selectedStoreCompetitors.filter(
                store => store.classification === CompetitorClassification.Direct
            ).length,
            indirectCompetitors: selectedStoreCompetitors.filter(
                store => store.classification === CompetitorClassification.Indirect
            ).length
        };

        return selectedStoreCount;
    }
);

export const selectIsFilterModified = createSelector(
    selectIsLoading,
    selectHasErrors,
    selectClassifiedCompetitors,
    selectCompetitorStoreCategories,
    selectShowAlternateCategoryCompetitors,
    (isLoading, hasErrors, classifiedCompetitors, competitorStoreCategories, showAlternateCategoryCompetitors) => {
        if (isLoading || hasErrors || !classifiedCompetitors) {
            return false;
        }

        const isCompetitorStoreCategoriesModified = competitorStoreCategories.filter(category => category.isPrimary && category.isSelected()).length < 1 ? true : false
            || competitorStoreCategories.filter(category => !category.isPrimary && category.isSelected()).length > 0 ? true : false;

        if (isCompetitorStoreCategoriesModified || showAlternateCategoryCompetitors !== false) {
            return true;
        }

        return false;
    }
);

export default competitionSlice;
